import * as React from 'react'
import {MouseEventHandler, ReactElement, ReactNode} from 'react'
import styled from 'styled-components'
import {Colors, FontWeights} from '../../../../styledComponents/Constants'
import {Box, Menu, MenuItem} from '@mui/material'
import {
    CenterRowFlexContainer,
    FullWidthLeftColumnFlexContainer,
    FullWidthRowFlexContainer,
    LeftRowFlexContainer,
    LeftTopRowFlexContainer,
    RowFlexContainer
} from '../../../../styledComponents/FlexComponents'
import {IconBox, UserIconLarge} from '../../../../components/assocDirector/AssocDirectorCommon'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faAngleDown, faAngleUp, faEllipsisH, faPen, faReply, faTrash} from '@fortawesome/free-solid-svg-icons'
import {
    ASSOC_GROUP,
    AVAILABLE_OR_NOT,
    HAVE_OR_NOT,
    ROLE_FOR_ADMINISTRATOR_REAL,
    ROLE_TYPE,
    VIEW_DO_OR_NOT,
} from '../../../../CodeMaster'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {Moment} from 'moment/moment'
import moment from 'moment'
import {IconProp} from '@fortawesome/fontawesome-svg-core'
import {Nullable} from '../../../../app/TypeHelpers'
import {HKConstants} from '../../../../HKConstants'
import {Comment, CommentInfo} from '../repository/NatGetDisasterCommentForAdmin'
import {NatPostDisasterCommentForAdminRequest} from '../repository/NatPostDisasterCommentForAdmin'
import {NatPostDisasterCommentFileUploadRequest} from '../repository/NatPostDisasterCommentFileUpload'
import {convertToRoomNumberForResident, judgeAuthorityCheck} from '../../../common/util/Natutil'
import {NatDisasterHelper} from '../common/NatDisasterHelper'
import NatAdminDisasterCommentInputForm, {DisasterCommentFile} from './NatAdminDisasterCommentInputForm'
import {StyledLink} from '../../../../styledComponents/adminPage/Links'
import {NatPutDisasterCommentForAdminRequest} from '../repository/NatPutDisasterCommentForAdmin'
import {InputError} from './NatAdminDisasterCommentError'

interface Props {
    commentInfo: CommentInfo
    currentDateTime: Moment
    pastFlag: boolean
    onClickReplyButton: (
        commentRequest: NatPostDisasterCommentForAdminRequest,
        fileRequests: NatPostDisasterCommentFileUploadRequest[]
    ) => Promise<Nullable<InputError>>
    onClickEditButton: (
        commentId: number,
        commentRequest: NatPutDisasterCommentForAdminRequest,
        fileRequests: NatPostDisasterCommentFileUploadRequest[]
    ) => Promise<Nullable<InputError>>
    onClickDeleteButton: (comment: Comment) => void
    onClickPreviewButton: (comment: Comment, startIndex: number) => void
    inputError: Nullable<InputError>
}

interface State {
    isOpen: boolean
    commentState: CommentState
    inputtingComment: string
    inputtingCommentFileList: DisasterCommentFile[]
    editingCommentId: Nullable<number>
    selectCommentId: Nullable<number>
    deleteFileNumberList: number[]
    inputError: Nullable<InputError>
    commentMenuButton: Nullable<HTMLElement>
}

type CommentState = 'reply' | 'edit-main' | 'edit-reply' | 'none'

export default class NatAdminDisasterCommentItem extends React.PureComponent<Props, State> {
    isPostAuthority: boolean
    isDeleteAuthority: boolean

    constructor(props: Readonly<Props>) {
        super(props)

        this.state = {
            isOpen: true,
            commentState: 'none',
            inputtingComment: '',
            inputtingCommentFileList: [],
            editingCommentId: null,
            selectCommentId: null,
            deleteFileNumberList: [],
            inputError: null,
            commentMenuButton: null
        }
        this.isPostAuthority = judgeAuthorityCheck(HKConstants.CMN.AUTH.POST_COMMENT_DISASTER_INFORMATION_SHARING)
        this.isDeleteAuthority = judgeAuthorityCheck(HKConstants.CMN.AUTH.DELETE_COMMENT_DISASTER_INFORMATION_SHARING)

        this.handleClickRegisterReplyButton = this.handleClickRegisterReplyButton.bind(this)
        this.handleClickEditCommentButton = this.handleClickEditCommentButton.bind(this)
    }

    componentDidUpdate(prevProps: Props): void {
        const prevError = prevProps.inputError
        const currentError = this.props.inputError
        const prevReply = prevProps.commentInfo.reply ?? []
        const currentReply = this.props.commentInfo.reply ?? []

        // 返信コメントのInputエラーチェック
        if (currentReply.length > prevReply.length) {
            const newReply = currentReply[currentReply.length - 1]
            if (currentError && (newReply.commentId === currentError.commentId)) {
                const commentFileList: DisasterCommentFile[] = newReply.commentToAttachment.map(file => {
                    return {
                        fileNumber: file.seq,
                        fileName: '',
                        fileData: file.previewPath,
                        fileSize: 0
                    }
                })
                this.setState({
                    commentState: 'edit-reply',
                    editingCommentId: newReply.commentId,
                    inputtingComment: newReply.comment,
                    inputtingCommentFileList: commentFileList,
                    deleteFileNumberList: [],
                    inputError: currentError
                })
            }
        }

        // 主コメントのInputエラーチェック
        if (prevError && !currentError) {
            if (prevError.commentId !== this.props.commentInfo.commentId) {
                return
            }

            const commentFileList: DisasterCommentFile[] = this.props.commentInfo.commentToAttachment.map(file => {
                return {
                    fileNumber: file.seq,
                    fileName: '',
                    fileData: file.previewPath,
                    fileSize: 0
                }
            })
            this.setState({
                commentState: 'edit-main',
                editingCommentId: this.props.commentInfo.commentId,
                inputtingComment: this.props.commentInfo.comment,
                inputtingCommentFileList: commentFileList,
                deleteFileNumberList: [],
                inputError: prevError
            })
        }
    }

    handleOpen = async () => {
        this.setState({isOpen: true})
    }

    handleClose = (): void => {
        this.setState({
            isOpen: false,
            commentState: this.state.commentState !== 'edit-main' ? 'none' : this.state.commentState
        })
    }

    handleReplay = (): void => {
        this.setState({
            commentState: 'reply',
            inputtingComment: '',
            inputtingCommentFileList: [],
            inputError: null
        })
    }

    handleInputCansel = (): void => {
        this.setState({
            commentState: 'none',
            editingCommentId: null,
            inputtingComment: '',
            inputtingCommentFileList: [],
            deleteFileNumberList: [],
            inputError: null
        })
    }

    async handleClickRegisterReplyButton(): Promise<void> {
        const {commentId} = this.props.commentInfo
        const {inputtingComment, inputtingCommentFileList} = this.state

        const fileRequests: NatPostDisasterCommentFileUploadRequest[] =
            inputtingCommentFileList.map(file => {
                const data: string[] = String(file.fileData).split(',')
                return {fileName: file.fileName, fileData: data[1]}
            })
        const commentRequest: NatPostDisasterCommentForAdminRequest = {
            replySourceId: commentId,
            comment: inputtingComment,
            imageAttachedFlag: fileRequests.length === 0 ?
                VIEW_DO_OR_NOT.HIDDEN.value : VIEW_DO_OR_NOT.DISPLAY.value
        }

        const inputError = await this.props.onClickReplyButton(commentRequest, fileRequests)
        if (inputError) {
            this.setState({inputError: inputError})
        } else {
            this.setState({
                isOpen: true,
                commentState: 'none',
                inputtingComment: '',
                inputtingCommentFileList: [],
                editingCommentId: null,
                deleteFileNumberList: [],
                inputError: null
            })
        }
    }

    async handleClickEditCommentButton(commentInfo: Comment): Promise<void> {
        const {inputtingComment, inputtingCommentFileList, deleteFileNumberList} = this.state

        const commentRequest: NatPutDisasterCommentForAdminRequest = {
            comment: inputtingComment,
            deleteCommentFile: deleteFileNumberList.map(file => {
                return {seq: file}
            }),
            version: commentInfo.version
        }

        const fileRequests: NatPostDisasterCommentFileUploadRequest[] =
            inputtingCommentFileList.filter(file => !file.fileNumber).map(file => {
                const data: string[] = String(file.fileData).split(',')
                return {
                    fileName: file.fileName,
                    fileData: data[1]
                }
            })

        const inputError = await this.props.onClickEditButton(commentInfo.commentId, commentRequest, fileRequests)
        if (inputError) {
            this.setState({
                editingCommentId: commentInfo.commentId,
                // inputErrorにfileNameListがある場合は既に編集処理は完了されている状態なので、削除ファイル番号リストは不要
                deleteFileNumberList: inputError.fileNameList ? [] : this.state.deleteFileNumberList,
                inputtingCommentFileList:
                    inputtingCommentFileList.filter(file => !inputError.fileNameList?.includes(file.fileName)),
                inputError: {
                    ...inputError,
                    commentId: commentInfo.commentId
                }
            })
        } else {
            this.setState({
                commentState: 'none',
                inputtingComment: '',
                inputtingCommentFileList: [],
                editingCommentId: null,
                deleteFileNumberList: [],
                inputError: null
            })
        }
    }

    handleChangeCommentInput = (
        comment: string,
        commentFileList: DisasterCommentFile[],
        deleteFileNumberList: number[],
        inputError: Nullable<InputError>
    ): void => {
        this.setState({
            inputtingComment: comment,
            inputtingCommentFileList: [...commentFileList],
            deleteFileNumberList: deleteFileNumberList,
            inputError: inputError
        })
    }

    handleEditClick = (comment: Comment, commentType: string): void => {
        const commentFileList: DisasterCommentFile[] = comment.commentToAttachment.map(file => {
            return {
                fileNumber: file.seq,
                fileName: '',
                fileData: file.previewPath,
                fileSize: 0
            }
        })

        this.setState({
            commentState: commentType === 'main' ? 'edit-main' : 'edit-reply',
            inputtingComment: comment.comment,
            inputtingCommentFileList: commentFileList,
            editingCommentId: comment.commentId,
            deleteFileNumberList: [],
            commentMenuButton: null,
            inputError: null
        })
    }

    handleDeleteClick = (comment: Comment): void => {
        this.setState({
            commentMenuButton: null
        })
        this.props.onClickDeleteButton(comment)
    }

    handlePreviewClick = (comment: Comment, startIndex: number): void => {
        this.props.onClickPreviewButton(comment, startIndex)
    }

    handleClickCommentMenuButton = (event: React.MouseEvent<HTMLElement>, commentId: number): void => {
        this.setState({
            selectCommentId: commentId,
            deleteFileNumberList: [],
            commentMenuButton: event.currentTarget
        })
    }

    handleCloseCommentMenu = (): void => {
        this.setState({
            selectCommentId: null,
            deleteFileNumberList: [],
            commentMenuButton: null
        })
    }

    renderReplyLink(): ReactElement {
        const {pastFlag} = this.props
        const {reply} = this.props.commentInfo
        const {isOpen, commentState} = this.state

        let linkNode: ReactNode
        if (reply.length > 0) {
            linkNode = isOpen ?
                this.renderLinkButtonFunction(this.handleClose, '返信を閉じる', faAngleUp) :
                this.renderLinkButtonFunction(this.handleOpen, `返信を見る(${reply.length}件)`, faAngleDown)
        } else {
            linkNode = (!pastFlag && this.isPostAuthority && commentState !== 'reply') ?
                this.renderLinkButtonFunction(this.handleReplay, '返信', faReply) : ''
        }
        return <>{linkNode}</>
    }

    renderEditCommentInputForm(commentInfo: Comment): ReactNode {
        const {inputtingComment, inputtingCommentFileList, deleteFileNumberList, inputError} = this.state
        return (
            <>
                <NatAdminDisasterCommentInputForm
                    type={'edit-'}
                    commentInfo={commentInfo}
                    submitButtonLabel={`完了`}
                    clearButtonLabel={'キャンセル'}
                    onClickEditButton={this.handleClickEditCommentButton}
                    onClickCancelButton={this.handleInputCansel}
                    onChange={this.handleChangeCommentInput}
                    inputtingComment={inputtingComment}
                    inputtingCommentFileList={inputtingCommentFileList}
                    deleteFileNumberList={deleteFileNumberList}
                    inputError={inputError}
                />
            </>
        )
    }

    renderLinkButtonFunction(onClick: MouseEventHandler, text: string, icon: IconProp): ReactElement {
        return (
            <>
                <BlockSpacer height={'10px'}/>
                <SmallStyledLink onClick={onClick}>
                    <LeftRowFlexContainer>
                        <IconBox><FontAwesomeIcon icon={icon}/></IconBox>
                        {text}
                    </LeftRowFlexContainer>
                </SmallStyledLink>
            </>
        )
    }

    renderPoster(commentInfo: Comment): ReactElement {
        const {
            posterFamilyName,
            posterFirstName,
            posterRidgeName,
            posterRoomNumber,
            posterRoleId
        } = commentInfo

        const assocGroupValues = ASSOC_GROUP.values().map(assoc => assoc.value)
        const isResident = posterRoleId.startsWith(ROLE_TYPE.RESIDENT.value)
        const isAssoc = assocGroupValues.includes(posterRoleId)
        const roomInfo = `${posterRidgeName} ${convertToRoomNumberForResident(posterRoomNumber)}号室`.trimStart()
        const adminRoleLabel = !isResident ? ROLE_FOR_ADMINISTRATOR_REAL.codeToEnumConstants(posterRoleId).label : ''
        const adminUserName = posterFamilyName + ' ' + posterFirstName + '（' + adminRoleLabel + '）'

        if (isResident || isAssoc) {
            return (
                <>
                    <PosterNameText>
                        {posterFamilyName + ' ' + posterFirstName}
                    </PosterNameText>
                    {isAssoc && <PosterNameText>（管理組合役員）</PosterNameText>}
                    <RoomNumberText>
                        {roomInfo}
                    </RoomNumberText>
                </>
            )
        } else {
            return (
                <PosterNameText>
                    {adminUserName}
                </PosterNameText>
            )
        }
    }

    renderCommentHeader(commentInfo: Comment, commentType: string, isEditing: boolean = false): ReactElement {
        const {currentDateTime} = this.props
        const {
            commentId,
            postDatetime,
            deleterRoleId,
            isDeleterSelf,
            editAvailability,
            isEdited
        } = commentInfo
        const {selectCommentId, commentMenuButton} = this.state

        let formatPosterDate = NatDisasterHelper.formatDoNotDisplayCurrentYear(currentDateTime, moment(postDatetime))
        if (isEdited === HAVE_OR_NOT.HAVE.value) {
            formatPosterDate = `${formatPosterDate} 編集済み`
        }

        if (deleterRoleId) {
            return (
                <CommentHeader>
                    <RowFlexContainer>
                        <IconWithCircleStyle>
                            <FontAwesomeIcon icon={faTrash}/>
                        </IconWithCircleStyle>
                        <Box ml={'15px'} flex={1}>
                            <Box component={'span'} fontSize={'14px'}>
                                {NatDisasterHelper.getDeleteMessage(deleterRoleId, isDeleterSelf)}
                            </Box>
                        </Box>
                    </RowFlexContainer>
                </CommentHeader>
            )
        }

        const isEditable = this.isPostAuthority && editAvailability === AVAILABLE_OR_NOT.AVAILABLE.value
        const isDeletable = judgeAuthorityCheck(HKConstants.CMN.AUTH.DELETE_COMMENT_DISASTER_INFORMATION_SHARING)
        return (
            <FullWidthRowFlexContainer>
                <CommentHeader>
                    <CardContainer>
                        <UserIconLarge/>
                        <Box width={'15px'}/>
                        <PosterContainer>
                            <Box lineHeight={'18px'}>
                                {this.renderPoster(commentInfo)}
                            </Box>
                            <CommentDatetime>
                                {formatPosterDate}
                            </CommentDatetime>
                        </PosterContainer>
                        <div>
                            {!isEditing && (isEditable || isDeletable) &&
                                <StyledLink
                                    id={`commentMenuButton-${commentId}`}
                                    onClick={(e) => this.handleClickCommentMenuButton(e, commentId)}
                                >
                                    <FontAwesomeIcon icon={faEllipsisH} color={Colors.AccentDark}/>
                                </StyledLink>
                            }
                            <Menu
                                open={commentMenuButton !== null && selectCommentId === commentId}
                                onClose={this.handleCloseCommentMenu}
                                anchorEl={commentMenuButton}
                            >
                                {isEditable &&
                                    <MenuItem onClick={() => this.handleEditClick(commentInfo, commentType)}>
                                        <LeftRowFlexContainer>
                                            <FontAwesomeIcon icon={faPen}/>
                                            <Box width={'15px'}/>
                                            コメントを編集
                                        </LeftRowFlexContainer>
                                    </MenuItem>
                                }
                                {isDeletable &&
                                    <MenuItem onClick={() => this.handleDeleteClick(commentInfo)}>
                                        <LeftRowFlexContainer>
                                            <FontAwesomeIcon icon={faTrash}/>
                                            <Box width={'15px'}/>
                                            コメントを削除
                                        </LeftRowFlexContainer>
                                    </MenuItem>
                                }
                            </Menu>
                        </div>
                    </CardContainer>
                </CommentHeader>
            </FullWidthRowFlexContainer>
        )
    }

    renderReadCount(readCount: string): ReactNode {
        return (
            <>
                <BlockSpacer height={'10px'}/>
                <Box textAlign={'right'} marginTop={'auto'} width={'100%'}>
                    既読：{readCount}
                </Box>
            </>
        )
    }

    renderComment(commentInfo: Comment): ReactElement {
        const {pastFlag} = this.props
        const {comment, commentToAttachment, readCount} = commentInfo

        if (!comment) {
            return <></>
        }

        return (
            <FullWidthLeftColumnFlexContainer>
                <BlockSpacer height={'5px'}/>
                <CommentContainer>{comment}</CommentContainer>
                {commentToAttachment.length > 0 &&
                    <ThumbnailImageContainer>
                        {commentToAttachment.map((file, index) => {
                            return (
                                <ThumbnailItem key={index} onClick={() => this.handlePreviewClick(commentInfo, index)}>
                                    <ThumbnailImage src={file.thumbnailPath}/>
                                </ThumbnailItem>
                            )
                        })}
                    </ThumbnailImageContainer>
                }
                {!pastFlag &&
                    this.renderReadCount(readCount)
                }
            </FullWidthLeftColumnFlexContainer>
        )
    }

    renderMainComment(): ReactNode {
        const {commentInfo} = this.props
        const {commentState, editingCommentId} = this.state

        return (
            commentState === 'edit-main' && (editingCommentId === commentInfo.commentId)
                ? <>
                    {this.renderCommentHeader(commentInfo, 'main', true)}
                    <Box ml={'47px'}>
                        {this.renderEditCommentInputForm(commentInfo)}
                        {this.renderReadCount(commentInfo.readCount)}
                        {this.renderReplyLink()}
                    </Box>
                </>
                : <>
                    {this.renderCommentHeader(commentInfo, 'main')}
                    <Box ml={'47px'}>
                        {this.renderComment(commentInfo)}
                        {this.renderReplyLink()}
                    </Box>
                </>
        )
    }

    renderReplyComment(replyCommentInfo: Comment, lastIndex: boolean): ReactNode {
        const {pastFlag} = this.props
        const {commentState, editingCommentId} = this.state
        const displayVerticalDivider =
            !(lastIndex && (pastFlag || !this.isPostAuthority || replyCommentInfo.deleterRoleId))

        return (
            commentState === 'edit-reply' && (editingCommentId === replyCommentInfo.commentId)
                ? <>
                    <BlockSpacer height={'10px'}/>
                    <StretchRowFlexContainer>
                        {this.renderCommentHeader(replyCommentInfo, 'reply', true)}
                    </StretchRowFlexContainer>
                    <StretchRowFlexContainer>
                        {displayVerticalDivider &&
                            <VerticalDividerContainer>
                                <VerticalDivider/>
                            </VerticalDividerContainer>
                        }
                        <Box ml={'15px'} width={'100%'}>
                            {this.renderEditCommentInputForm(replyCommentInfo)}
                            {this.renderReadCount(replyCommentInfo.readCount)}
                        </Box>
                    </StretchRowFlexContainer>
                </>
                : <>
                    <BlockSpacer height={'10px'}/>
                    <StretchRowFlexContainer>
                        {this.renderCommentHeader(replyCommentInfo, 'reply')}
                    </StretchRowFlexContainer>
                    <StretchRowFlexContainer>
                        {displayVerticalDivider &&
                            <VerticalDividerContainer>
                                <VerticalDivider/>
                            </VerticalDividerContainer>
                        }
                        <Box ml={displayVerticalDivider ? '15px' : '47px'}/>
                        {this.renderComment(replyCommentInfo)}
                    </StretchRowFlexContainer>
                </>
        )
    }

    render(): ReactElement {
        const {pastFlag, commentInfo} = this.props
        const {isOpen, commentState} = this.state
        const {reply} = this.props.commentInfo
        const displayVerticalDivider = reply?.length > 0 && reply[reply.length - 1].deleterRoleId !== null

        return (
            <CommentArea>
                {this.renderMainComment()}
                {isOpen && reply.length > 0 &&
                    <Box ml={'47px'}>
                        {reply.map((replyCommentInfo, index) => {
                            return (
                                <React.Fragment key={replyCommentInfo.commentId}>
                                    {this.renderReplyComment(replyCommentInfo, reply.length - 1 === index)}
                                </React.Fragment>
                            )
                        })}
                        {(!pastFlag && this.isPostAuthority && commentState !== 'reply') &&
                            <>
                                {this.renderLinkButtonFunction(this.handleReplay, '返信', faReply)}
                            </>
                        }
                    </Box>
                }
                {(this.isPostAuthority && commentState === 'reply') &&
                    <>
                        <Box ml={'47px'}>
                            {displayVerticalDivider &&
                                <StretchRowFlexContainer>
                                    <VerticalDividerContainer>
                                        <VerticalDivider/>
                                    </VerticalDividerContainer>
                                </StretchRowFlexContainer>
                            }
                            <BlockSpacer height={'14px'}/>
                            <NatAdminDisasterCommentInputForm
                                type={'reply-'}
                                replySourceId={commentInfo.commentId}
                                submitButtonLabel={`返信する`}
                                clearButtonLabel={'キャンセル'}
                                onClickSubmitButton={this.handleClickRegisterReplyButton}
                                onClickCancelButton={this.handleInputCansel}
                                onChange={this.handleChangeCommentInput}
                                inputtingComment={this.state.inputtingComment}
                                inputtingCommentFileList={this.state.inputtingCommentFileList}
                                deleteFileNumberList={[]}
                                inputError={this.state.inputError}
                            />
                        </Box>
                    </>
                }
            </CommentArea>
        )
    }
}

const CommentArea = styled.div`
    padding: 10px 0;

    &:first-child {
        padding-top: 0;
    }

    &:last-child {
        padding-bottom: 0;
    }
`

const SmallStyledLink = styled(StyledLink)`
    &.MuiTypography-body2 {
        font-size: 14px;
    }
`

const CommentHeader = styled(LeftTopRowFlexContainer)`
    width: 100%;
    min-height: 39px;
    align-items: center;
`

const CardContainer = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
`

const PosterContainer = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
`

const PosterNameText = styled.span`
    font-weight: ${FontWeights.Bold};
    font-size: 14px;
`

const RoomNumberText = styled.span`
    white-space: pre-wrap;
    padding-left: 5px;
    font-size: 12px;
    line-height: 18px;
    color: ${Colors.AccentDark};
`

const CommentDatetime = styled.div`
    font-size: 12px;
    color: ${Colors.AccentDark};
`

const StretchRowFlexContainer = styled(LeftRowFlexContainer)`
    align-items: stretch;
`

const VerticalDividerContainer = styled(CenterRowFlexContainer)`
    align-items: stretch;
    min-width: 32px;
    min-height: 29px;
`

const VerticalDivider = styled.hr`
    margin: 0;
    width: 3px;
    height: 100%;
    border: none;
    border-radius: 4px;
    background-color: ${Colors.Background};
`

const IconWithCircleStyle = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 32px;
    height: 32px;
    font-size: 18px;
    color: #BDBDBD;

    background: #FFFFFF;
    border: 1px solid #D9D9D9;
    box-sizing: border-box;
    border-radius: 50%;
`

const CommentContainer = styled.div`
    white-space: pre-wrap;
    overflow-wrap: anywhere;
`

const ThumbnailImageContainer = styled.div`
    display: flex;
    gap: 5px;
    padding-top: 5px;
    flex-wrap: wrap;
`

const ThumbnailItem = styled.div`
    cursor: pointer;
`

const ThumbnailImage = styled.img`
    display: block;
    width: 134px;
    height: 134px;
    background-color: ${Colors.AccentLight};
    object-fit: contain;
`
